import { type FC, useContext, useEffect, useRef, useState } from 'react';
import Card from 'react-bootstrap/Card';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';

import './styles/styles.css';
import { Button, Col, Overlay, Popover } from 'react-bootstrap';
import { PlusSquareFill } from 'react-bootstrap-icons';
import type { Option } from 'react-multi-select-component';
import CheckIcon from '../../../assets/check-checkbox.svg';
import DropDownIcon from '../../../assets/drop-down-icon.svg';
import DropDownOpenIcon from '../../../assets/drop-down-open.svg';
import UnCheckIcon from '../../../assets/uncheck-checkbox.svg';
import { PackageContext } from '../../../modules/context/packageContext';
import { PackageStatus } from '../../../modules/enums/status';
import type { PackagesResponse } from '../../../modules/interfaces';
import { Can } from '../../../modules/utils/can';
import { PackageCard } from '../../Cards/PackagesCard/PackageCard';
import { AddPackageModal } from '../../Modals/PackagesModals/AddPackageModal';
import { EarlyCheckInModal } from '../../Modals/PackagesModals/EarlyCheckInModal';
import { PackagePriorityModal } from './PackagesPriorityModal';

interface PackagesStatusInterface {
  label: string;
  value: string;
}

const categoriesData: PackagesStatusInterface[] = [
  { label: 'Enabled', value: 'ACTIVE' },
  { label: 'Disabled', value: 'INACTIVE' },
];
export const PackagesManagement: FC = () => {
  const [packages, setPackages] = useState<PackagesResponse[]>([]);
  const [showAddPackageModal, setShowAddPackageModal] = useState(false);
  const [showEarlyCheckModal, setShowEarlyCheckModal] = useState(false);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [packageValues, setPackageValues] = useState<PackagesResponse>({});
  const [statusShow, setStatusShow] = useState(false);
  const statusTarget = useRef(null);
  const [status, setStatus] = useState<string[]>([]);

  const [EarlyCheckParams, setEarlyCheckParams] = useState<Option[]>([]);

  const value = useContext(PackageContext);

  useEffect(() => {
    if (!value?.packages || value?.loading) return;

    const { packages: packagesValue } = value;

    setPackages(packagesValue);
  }, [value?.packages, value?.loading]);

  const getEarlyCheckParams = (values: PackagesResponse, params: Option[]) => {
    setPackageValues(values);
    setEarlyCheckParams(params);
    setShowEarlyCheckModal(true);
  };
  const resetEarlyCheckParams = () => {
    setPackageValues({});
    setEarlyCheckParams([]);
    setShowEarlyCheckModal(true);
    setShowEarlyCheckModal(false);
  };

  const onStatusItemClick = (statusItem: string, isSelected: boolean) => {
    if (isSelected) {
      const newList = status.filter((item) => item !== statusItem);
      setStatus(newList);
    } else {
      const newList = [...status];
      newList.push(statusItem);
      setStatus(newList);
    }
  };

  const renderStatusItem = (statusItem: PackagesStatusInterface) => {
    const isSelected = status.includes(statusItem.value);
    return (
      <div
        className="drop-down-status-item"
        style={{ backgroundColor: isSelected ? '#F6F6F6' : '#FFFFFF' }}
        onClick={() => onStatusItemClick(statusItem.value, isSelected)}
      >
        <img
          className="checkbox-icon"
          src={isSelected ? CheckIcon : UnCheckIcon}
          alt="check-icon"
        />
        <div>{statusItem.label}</div>
      </div>
    );
  };

  const onApplyStatus = () => {
    let filterStatus: string | undefined;

    if (status.length === 1) {
      filterStatus = status[0];
    } else if (status.length === 0) {
      filterStatus = PackageStatus.ACTIVE;
    }
    value?.refetch({ params: { status: filterStatus } });
  };

  const onCancelStatus = () => {
    setStatus([]);
    setStatusShow(false);
  };

  return (
    <>
      <Container className="table-header" fluid>
        <Row className="m-0 justify-content-end w-100 align-items-center">
          <Col className="p-0">
            <div className="mt-16px header-title-text">Add-ons list</div>
          </Col>
          <Col className="p-0" style={{ display: 'flex', justifyContent: 'end' }}>
            <div>
              <Button onClick={() => setShowPriorityModal(true)}>Edit priorities</Button>
            </div>
            <div className="artical-dropdown-container ">
              <div
                ref={statusTarget}
                onClick={() => setStatusShow(!statusShow)}
                className="category-text-container"
              >
                <div className="more-text-in-dropdown">
                  {status.length === 0 && 'Filter by status'}
                  {status.length === 1 &&
                    `${status[0] === PackageStatus.ACTIVE ? 'Enabled' : 'Disabled'}`}
                  {status.length === 2 && 'Enabled and Disabled'}
                </div>

                <img src={statusShow ? DropDownOpenIcon : DropDownIcon} alt="drop-down" />
              </div>
            </div>
            <Overlay
              onHide={() => setStatusShow(false)}
              target={statusTarget.current}
              rootClose
              show={statusShow}
              placement="bottom"
            >
              <Popover id="popover-positioned-bottom">
                <Popover.Body className="property_overlay">
                  <div className="drop-down-container-status">
                    {categoriesData.map(renderStatusItem)}
                    <div className="apply-and-cancle-container">
                      <div onClick={onCancelStatus} className="cancle-text-container">
                        Cancel
                      </div>
                      <div onClick={onApplyStatus} className="apply-text-container">
                        Apply
                      </div>
                    </div>
                  </div>
                </Popover.Body>
              </Popover>
            </Overlay>
          </Col>
        </Row>
      </Container>
      <Container fluid className="mt-16">
        <Row>
          <Can I="create" a="packages">
            <Col xs={6} sm={4} lg={3} xl={2} style={{ marginTop: 0 }}>
              <Card
                className="package-card new"
                onClick={() => setShowAddPackageModal(true)}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'Center',
                  cursor: 'pointer',
                }}
              >
                <PlusSquareFill data-testid="plusSquareId" size={30} color="#14BF59" />

                <span style={{ marginTop: '10px' }}>Add</span>
              </Card>
            </Col>
          </Can>
          {packages.map((item) => (
            <Col key={item.uuid} xs={6} sm={4} lg={3} xl={2}>
              <PackageCard
                title={item?.name || ''}
                subtitle="Add-on"
                values={item}
                refreshPackages={onApplyStatus}
                imageUrl={item?.media?.[0]?.uri || '/no_image.jpg'}
              />
            </Col>
          ))}
        </Row>
      </Container>

      <AddPackageModal
        show={showAddPackageModal}
        handleClose={() => {
          setShowAddPackageModal(false);
        }}
        getEarlyCheckParams={getEarlyCheckParams}
      />
      <EarlyCheckInModal
        show={showEarlyCheckModal}
        handleClose={resetEarlyCheckParams}
        values={packageValues}
        refreshPackages={onApplyStatus}
        params={EarlyCheckParams}
      />
      {packages && (
        <PackagePriorityModal
          show={showPriorityModal}
          handleClose={() => {
            setShowPriorityModal(false);
          }}
          packagesData={packages}
          onSavePriority={onApplyStatus}
        />
      )}
    </>
  );
};
